/** @flow */

import React, { Fragment } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { toast } from "react-toastify";
import { Pagination, GSGrid, TooltipDropdown, View } from "@components";
import { formatDate, formatDateTime, transformSecondsInHours } from "@transformers/date";
import { AddEditEmployeePositionModal } from "@modules/employee/components";
import actionCreators from "@modules/employee/actionCreator";
import type { State } from "@store/reducers";
import type { PropertiesSchema, PaginationExtendedType } from "@types";
import type { EmployeeType } from "@modules/employee/types";
import * as employeesSelectors from "@modules/employee/selectors";
import config from "@config";
import { route } from "@routes/helper";
import type {
  RoleSummary as RoleSummaryType,
  PositionLinkedAbsence,
} from "@modules/employee/reducer/employment-details/types";
import {
  transformToSchemaFormData,
  transformToRequestData,
} from "@modules/employee/components/role-summary/transformers";
import "./styles.scss";

type OwnProps = $Exact<{
  clientId: ?number,
  employeeId: ?number,
  pagination?: $Exact<{
    pageNumber: number,
    totalPages: number,
    pageSize: number,
    totalElements: number,
  }>,
  schema: ?PropertiesSchema,
  roleSummary: Array<RoleSummaryType>,
  onChange: () => void,
  handleDeleteEmployeePosition: Function,
  canDeletePosition: boolean,
  canRunDeleteQuery: boolean,
}>;

type PropsFromState = $Exact<{
  isEditEmployeePositionModalVisible: boolean,
  formData: Object,
  employee: ?EmployeeType,
  pagination: PaginationExtendedType,
  linkedAbsences: ?Array<PositionLinkedAbsence>,
}>;

type PropsFromDispatch = $Exact<{
  onUpdateEmployeePosition: typeof actionCreators.UpdateEmployeePosition,
  onEditEmployeePositionHandler: typeof actionCreators.ToggleEditEmployeePositionModal,
  onDismissEditEmployeePositionHandler: typeof actionCreators.ToggleEditEmployeePositionModal,
  onGetPositionLinkedAbsences: typeof actionCreators.GetPositionLinkedAbsences,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class RoleSummaryList extends React.Component<Props> {
  employeePositionShiftPatterData: *;

  editEmployeePositionHandler = (formData: Object) => {
    this.employeePositionShiftPatterData = formData.shiftPattern;
    const employeePositionData = transformToSchemaFormData(formData);

    this.props.onEditEmployeePositionHandler({
      formData: employeePositionData,
      visible: true,
    });

    this.props.onGetPositionLinkedAbsences({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      positionId: formData.id,
    });
  };

  updateEmployeePosition = (formData: Object) => {
    const employeePositionData = transformToRequestData(formData);

    this.props.onUpdateEmployeePosition({
      formData: employeePositionData,
      positionId: formData.id,
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  };

  get roleSummaryColumns(): Object[] {
    const res = [
      {
        key: "id",
        name: (
          <TooltipDropdown
            title={this.props.t("ID")}
            description={this.props.t("POSITION_ID_TOOLTIP_TEXT")}
            id="position-id"
          >
            {this.props.t("ID")}
          </TooltipDropdown>
        ),
      },
      {
        key: "employeePositionReference",
        name: (
          <TooltipDropdown
            title={this.props.t("POSITION_REF")}
            description={this.props.t("POSITION_REF_TOOLTIP_TEXT")}
            id="position-ref"
          >
            {this.props.t("POSITION_REF")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => row.employeePositionReference,
      },
      {
        key: "jobTitle",
        name: (
          <TooltipDropdown
            title={this.props.t("JOB_TITLE")}
            description={this.props.t("JOB_TITLE_TOOLTIP_TEXT")}
            id="job-title"
          >
            {this.props.t("JOB_TITLE")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => (row || {}).jobTitle,
        align: "left",
      },
      {
        key: "department",
        name: (
          <TooltipDropdown
            title={this.props.t("DEPARTMENT")}
            description={this.props.t("DEPARTMENT_TOOLTIP_TEXT")}
            id="department-role-summary"
          >
            {this.props.t("DEPARTMENT")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => ((row || {}).organisationalUnit || {}).name,
        align: "left",
      },
      {
        key: "weeklyContractedHours",
        name: (
          <TooltipDropdown
            title={this.props.t("WEEKLY_HOURS")}
            description={this.props.t("WEEKLY_HOURS_TOOLTIP_TEXT")}
            id="weekly-contracted-hours-role-summary"
          >
            {this.props.t("WEEKLY_HOURS")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => {
          return transformSecondsInHours((row || {}).weeklyContractedHoursInSeconds);
        },
      },
      {
        key: "daysPerYear",
        name: (
          <TooltipDropdown
            title={this.props.t("DAYS_PER_YEAR")}
            description={this.props.t("DAYS_PER_YEAR_TOOLTIP_TEXT")}
            id="days-per-year-role-summary"
            size="small"
          >
            {this.props.t("DAYS_PER_YEAR")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => (row || {}).daysPerYear,
      },
      {
        key: "fte",
        name: (
          <TooltipDropdown
            title={this.props.t("FTE")}
            description={this.props.t("FTE_TOOLTIP_TEXT")}
            id="fte-role-summary"
          >
            {this.props.t("FTE")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => (row || {}).fte,
      },
      {
        key: "activeFrom",
        name: (
          <TooltipDropdown
            title={this.props.t("POSITION_START_DATE")}
            description={this.props.t("EFFECTIVE_FROM_DATE_TOOLTIP_TEXT")}
            id="effective-from-date-summary"
          >
            {this.props.t("START_DATE")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => formatDate(row.activeFrom),
      },
      {
        key: "activeTo",
        name: (
          <TooltipDropdown
            title={this.props.t("POSITION_END_DATE")}
            description={this.props.t("EFFECTIVE_END_DATE_TOOLTIP_TEXT")}
            id="effective-end-date-summary"
          >
            {this.props.t("END_DATE")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => formatDate((row || {}).activeTo),
      },
      {
        key: "status",
        name: this.props.t("STATUS"),
        align: "center",
        renderCell: ({ row }: { row: any }) => (
          <div className={row.status === "ACTIVE" ? "active-badge" : "inactive-badge"}>
            {row.status === "ACTIVE" ? this.props.t("ACTIVE") : this.props.t("INACTIVE")}
          </div>
        ),
        renderCell: ({ row }: Object) => (
          <div className={row.status === "ACTIVE" ? "active-badge" : "inactive-badge"}>
            {row.status === "ACTIVE" ? this.props.t("ACTIVE") : this.props.t("INACTIVE")}
          </div>
        ),
      },
      {
        key: "staffGroup",
        name: (
          <TooltipDropdown
            className="tooltip-interactive"
            title={this.props.t("STAFF_GROUP")}
            descriptionWithLink={[
              {
                text: this.props.t("STAFF_GROUP_TOOLTIP_TEXT_1"),
                link: (
                  <a
                    href={route("ADMINISTRATION_SERVICE_SETTINGS", {
                      clientId: this.props.clientId,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary pointer"
                  >
                    {this.props.t("STAFF_GROUP_TOOLTIP_LINK_TEXT")}
                  </a>
                ),
                textSecondary: this.props.t("STAFF_GROUP_TOOLTIP_TEXT_2"),
              },
            ]}
            id="staff-group-role-summary"
          >
            {this.props.t("STAFF_GROUP")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: Object) => ((row || {}).staffGroup || {}).name,
        align: "left",
      },
    ];

    if (this.props.schema) {
      res.push({
        name: this.props.t("EDIT"),
        key: "edit",
        onCellClick: (row: Object) => this.editEmployeePositionHandler(row),
        renderCell: ({ row }: Object) => GSGrid.render.materialIcon("edit"),
      });
    }
    if (this.props.canDeletePosition) {
      res.push({
        name: this.props.t("DELETE"),
        key: "DELETE",
        onCellClick: (row: Object) => {
          if (this.props.canRunDeleteQuery) {
            this.props.handleDeleteEmployeePosition(row);
          } else {
            toast.error(this.props.t("YOU_DON_T_HAVE_PRIVILEGE_EMPLOYEE_DELETE"));
          }
        },
        renderCell: ({ row }: Object) => GSGrid.render.materialIcon("delete_forever", "negative"),
      });
    }

    return res;
  }

  get totalPages() {
    return this.props.pagination
      ? Math.ceil(this.props.pagination.totalElements / this.props.pagination.pageSize)
      : 0;
  }

  get schema() {
    const { schema } = this.props;
    // if the employee role have a custom shift patter
    // add it to the schema shift pattern list
    if (
      this.employeePositionShiftPatterData &&
      this.employeePositionShiftPatterData.patternType === "CUSTOM"
    ) {
      return {
        ...schema,
        properties: {
          ...schema?.properties,
          shiftPattern: {
            ...schema?.properties?.shiftPattern,
            oneOf: [...(schema?.properties?.shiftPattern.oneOf || [])].map(item => {
              if (item.const === -1) {
                return {
                  const: this.employeePositionShiftPatterData.id,
                  title: this.employeePositionShiftPatterData.name,
                };
              }
              return item;
            }),
          },
        },
      };
    }

    return schema;
  }

  render() {
    return (
      <View flex={1}>
        {this.props.roleSummary && (
          <GSGrid columns={this.roleSummaryColumns} rows={this.props.roleSummary || []} />
        )}
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={this.props.pagination.pageNumber}
          totalPages={this.props.pagination.totalPages}
          onChange={this.props.handlePageChange}
        />
        {this.props.schema && this.props.isEditEmployeePositionModalVisible && (
          <AddEditEmployeePositionModal
            onDismissHandler={this.props.onDismissEditEmployeePositionHandler}
            onSubmit={this.updateEmployeePosition}
            schema={this.schema}
            formData={this.props.formData}
            title={this.props.t("EDIT_ROLE")}
            submitButtonText={this.props.t("SAVE")}
            submitButtonIcon="save"
            employee={this.props.employee}
            linkedAbsences={this.props.linkedAbsences}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State, OwnProps: Object): PropsFromState => ({
  isEditEmployeePositionModalVisible: employeesSelectors.getIsEditEmployeePositionModalVisible(
    state,
  ),
  employee: employeesSelectors.getEmployee(OwnProps.employeeId)(state),
  formData: employeesSelectors.getEmployeePositionFormData(state),
  pagination: employeesSelectors.getRoleSummaryPagination(state),
  linkedAbsences: employeesSelectors.getPositionLinkedAbsences(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onUpdateEmployeePosition: actionCreators.UpdateEmployeePosition,
  onEditEmployeePositionHandler: actionCreators.ToggleEditEmployeePositionModal,
  onDismissEditEmployeePositionHandler: () =>
    actionCreators.ToggleEditEmployeePositionModal({ visible: false }),
  onGetPositionLinkedAbsences: actionCreators.GetPositionLinkedAbsences,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(RoleSummaryList));
